/**
 * Display copy for the account screen. **Not a fixture** — nothing here stands in for an API.
 *
 * The split is deliberate and it is the point of E35. The **server** owns every rule: which
 * alerts cannot be switched off and why, what a valid timezone is, which themes exist. This file
 * owns only the **wording** — the label on a row, the human name for a timezone — because
 * `enum_label` does not exist yet and a client has to call things something.
 *
 * ⛔ The one thing that must never come back here is a rule. `NOTIFY_GROUPS` used to live in
 * `src/fixtures/account.ts` and marked two rows `'locked'` with a hard-coded sentence explaining
 * why. That made the guarantee that a safety page and an audit-chain break reach every admin a
 * property of a console build. It is now `AdminNotificationTopic.locked_reason`, served.
 */
import type { IconName } from '../components/icons';

export const ACCOUNT_TABS = [
  { key: 'account', label: 'Account', icon: 'settings' },
  { key: 'security', label: 'Password & security', icon: 'key-round' },
  { key: 'notify', label: 'Notifications', icon: 'bell' },
  { key: 'appearance', label: 'Appearance', icon: 'palette' },
  { key: 'keys', label: 'Keyboard shortcuts', icon: 'keyboard' },
] as const;

/** A stored value and the words for it. The value is the server's; the words are ours. */
export interface Choice {
  value: string;
  label: string;
}

/** IANA names. The server accepts any the tz database knows — these are the two ops actually use. */
export const TIMEZONES: readonly Choice[] = [
  { value: 'Asia/Kolkata', label: 'India — Asia/Kolkata (GMT+5:30)' },
  { value: 'UTC', label: 'UTC' },
];

export const LANGUAGES: readonly Choice[] = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'हिन्दी (Hindi)' },
];

export const DENSITIES: readonly Choice[] = [
  { value: 'compact', label: 'Compact (36px rows)' },
  { value: 'comfortable', label: 'Comfortable (44px rows)' },
];

export const THEME_CHOICES = [
  { key: 'light', label: 'Light', note: 'Cream surfaces', icon: 'sun' },
  { key: 'dark', label: 'Dark', note: 'Dim, low glare', icon: 'moon' },
  /* Following the OS is a real preference and the design offers it. The console's own switch is
     still light/dark — `system` means "do not pin me", not a third palette. */
  { key: 'system', label: 'System', note: 'Match your OS', icon: 'monitor' },
] as const satisfies readonly { key: string; label: string; note: string; icon: IconName }[];

/**
 * Display preferences, saved to the ACCOUNT so they follow the person to another machine.
 *
 * ⛔ `key` is the field name in `PATCH /v1/admin/me` (E59 / G17a). They used to be per-render
 * state while their own docstring claimed otherwise — a toggle that forgets itself is a control
 * describing something the system does not do.
 */
export const DISPLAY_PREFS: readonly { key: 'reduce_motion' | 'mono_ids'; label: string; note: string }[] = [
  { key: 'reduce_motion', label: 'Reduce motion', note: 'Collapse every transition to a fade.' },
  { key: 'mono_ids', label: 'Ids and money in monospace', note: 'Codes, hashes and amounts render tabular.' },
];

/**
 * The words for each alert topic, grouped for the screen.
 *
 * ⛔ **No `value` and no `locked` here.** Both come from the server. A topic the server sends that
 * this build has no copy for is still rendered — its key is shown rather than dropped — because a
 * new alert silently missing from this page is exactly how somebody stops receiving one without
 * ever choosing to.
 */
export const TOPIC_COPY: Readonly<Record<string, { group: string; label: string; note: string }>> = {
  dispatch_unaccepted: {
    group: 'Dispatch',
    label: 'Unaccepted after the last wave',
    note: 'A booking runs out of dispatch waves',
  },
  dispatch_replacement_needed: {
    group: 'Dispatch',
    label: 'Replacement needed',
    note: 'A confirmed pandit drops out',
  },
  dispatch_escalation_assigned: {
    group: 'Dispatch',
    label: 'Escalation assigned to me',
    note: 'An ops escalation is routed to you',
  },
  money_four_eyes_waiting: {
    group: 'Money',
    label: 'Four-eyes request waiting',
    note: 'A payout, refund or write-off needs your countersign',
  },
  money_reconciliation_break: {
    group: 'Money',
    label: 'Reconciliation break opened',
    note: 'A daily close finds a difference',
  },
  money_payout_batch_executed: {
    group: 'Money',
    label: 'Payout batch executed',
    note: 'A batch leaves the settlement account',
  },
  supply_kyc_case_ageing: {
    group: 'Supply & support',
    label: 'KYC case waiting over 48 h',
    note: 'A review case is ageing',
  },
  support_safety_ticket: {
    group: 'Supply & support',
    label: 'Safety ticket raised',
    note: 'A safety ticket pages the on-call role',
  },
  support_sla_breach_warning: {
    group: 'Supply & support',
    label: 'SLA breach warning',
    note: 'A ticket is about to breach its pinned clock',
  },
  platform_deactivation_requested: {
    group: 'Platform',
    label: 'Deactivation requested',
    note: 'An admin asks a superadmin to deactivate their account',
  },
  platform_flag_flipped: {
    group: 'Platform',
    label: 'Feature flag flipped',
    note: 'Someone changes a flag',
  },
  platform_audit_chain_failed: {
    group: 'Platform',
    label: 'Audit chain verification failed',
    note: 'The hash chain reports a break',
  },
  platform_weekly_digest: {
    group: 'Platform',
    label: 'Weekly operations digest',
    note: 'A Monday summary by email',
  },
};

/** The order the groups appear in. A topic whose group is unknown falls to the end, under its key. */
export const TOPIC_GROUPS: readonly string[] = ['Dispatch', 'Money', 'Supply & support', 'Platform'];
